
import { SOCIAL_PLATFORMS_METADATA } from '@/constants';
import { AppRoute } from '@/router/routes';
import { SocialFeedItem, SocialFeedPlatform } from '@/types/socialFeed';
import {
  formatSocialFeedPublishedDate,
  socialFeedPlatformLabel,
} from '@/utils/socialFeed';
import { DEFAULT_TIMEZONE, SupportedPlatform } from 'ignite360-core';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component({ name: 'social-feed-items-view' })
export default class SocialFeedItemsView extends Vue {
  readonly instagramPlatform = SupportedPlatform.Instagram;
  readonly FEED_PREVIEW_LENGTH = 150;

  @Prop({ required: true, type: Array })
  readonly items!: SocialFeedItem[];

  @Prop({ required: false, type: String, default: '' })
  readonly platform!: SocialFeedPlatform | '';

  @Prop({ required: false, type: Boolean, default: false })
  readonly selectable!: boolean;

  @Prop({ required: false, type: Boolean, default: false })
  readonly interactive!: boolean;

  @Prop({ required: false, type: Boolean, default: false })
  readonly mixed!: boolean;

  @Prop({ required: false, type: Boolean, default: false })
  readonly global!: boolean;

  @Prop({ required: false, type: String, default: () => DEFAULT_TIMEZONE })
  readonly timezone!: string;

  get useListLayout(): boolean {
    return this.mixed || this.platform !== this.instagramPlatform;
  }

  get showPlatformInMeta(): boolean {
    return this.mixed || this.global;
  }

  itemKey(item: SocialFeedItem): string {
    return `${item.botId || 'local'}-${item.platform}-${item.id}`;
  }

  showProjectInMeta(item: SocialFeedItem): boolean {
    return this.global && !!this.projectLabel(item);
  }

  projectLabel(item: SocialFeedItem): string {
    return (item.botName || '').trim();
  }

  projectRoute(item: SocialFeedItem) {
    return {
      name: AppRoute.ProjectIntegrationSocialPosting,
      params: { id: item.botId },
    };
  }

  itemTimezone(item: SocialFeedItem): string {
    return item.botTimezone || this.resolvedTimezone;
  }

  get resolvedTimezone(): string {
    return this.timezone || DEFAULT_TIMEZONE;
  }

  isClickable(item: SocialFeedItem): boolean {
    return this.selectable || this.interactive || !!item.permalink;
  }

  wrapperTag(item: SocialFeedItem): string {
    if (this.selectable || this.interactive) {
      return 'button';
    }
    if (item.permalink) {
      return 'a';
    }
    return 'div';
  }

  wrapperAttrs(item: SocialFeedItem): Record<string, string> {
    if (this.selectable || this.interactive) {
      return { type: 'button' };
    }
    if (item.permalink) {
      return {
        href: item.permalink,
        target: '_blank',
        rel: 'noopener noreferrer',
      };
    }
    return {};
  }

  onItemClick(item: SocialFeedItem, event: Event) {
    if (this.selectable) {
      event.preventDefault();
      this.$emit('select', item);
      return;
    }
    if (this.interactive) {
      event.preventDefault();
      this.$emit('open', item);
    }
  }

  mediaTypeUpper(item: SocialFeedItem): string {
    return String(item.mediaType || '').toUpperCase();
  }

  isVideoMedia(item: SocialFeedItem): boolean {
    const t = this.mediaTypeUpper(item);
    return t === 'VIDEO' || t === 'REELS';
  }

  isCarouselMedia(item: SocialFeedItem): boolean {
    return this.mediaTypeUpper(item) === 'CAROUSEL_ALBUM';
  }

  captionPreview(item: SocialFeedItem): string {
    return this.truncateFeedText((item.content || item.title || '').trim() || 'Post');
  }

  feedContentPreview(item: SocialFeedItem): string {
    return this.truncateFeedText((item.content || item.title || '').trim() || 'No text content');
  }

  truncateFeedText(text: string): string {
    if (text.length <= this.FEED_PREVIEW_LENGTH) {
      return text;
    }
    return `${text.slice(0, this.FEED_PREVIEW_LENGTH).trimEnd()}…`;
  }

  ariaLabelFor(item: SocialFeedItem): string {
    const kind = this.isVideoMedia(item)
      ? 'Video'
      : this.isCarouselMedia(item)
        ? 'Carousel'
        : 'Photo';
    const cap = this.captionPreview(item);
    const prefix = this.selectable ? 'Select' : this.interactive ? 'Open' : 'View';
    return `${prefix} ${kind}: ${cap}`.slice(0, 200);
  }

  shouldShowTitleLine(item: SocialFeedItem): boolean {
    if (
      item.platform === SupportedPlatform.FacebookMessenger ||
      item.platform === SupportedPlatform.GoogleBusiness
    ) {
      return false;
    }
    const title = (item.title || '').trim();
    const content = (item.content || '').trim();
    if (!title) {
      return false;
    }
    return title !== content;
  }

  platformLabel(platform: SocialFeedPlatform): string {
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === platform);
    return meta?.label || socialFeedPlatformLabel(platform);
  }

  platformImage(platform: SocialFeedPlatform): string {
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === platform);
    return meta?.image || '';
  }

  postMetaDate(item: SocialFeedItem): string | null {
    return formatSocialFeedPublishedDate(item.publishedAt, this.itemTimezone(item));
  }

  showPostMeta(item: SocialFeedItem): boolean {
    if (this.showPlatformInMeta) {
      return true;
    }
    return !!this.postMetaDate(item);
  }

  postMetaLine(item: SocialFeedItem): string | null {
    return this.postMetaDate(item);
  }
}
